import { CATEGORIES } from '@kijiji/category'
import { type TFunction } from 'i18next'
import { type NextRouter } from 'next/router'
import qs from 'query-string'

import { getSrpComposedUrl } from '@/domain/srp/getSrpComposedUrl'
import {
  CAR_BODY_TYPE_VALUES,
  CAR_MAKE_VALUES,
  FUEL_VALUES,
} from '@/features/attributes/constants/attributes'
import {
  addAttributeParamToUrl,
  AttributeType,
} from '@/features/search/utils/vertical-brp/addAttributeParamToUrl'
import { addPriceParamToUrl } from '@/features/search/utils/vertical-brp/addPriceParamToUrl'
import { changeCategoryOfUrl } from '@/features/search/utils/vertical-brp/changeCategoryOfUrl'

type AutosCategoryData = {
  imgSrc: string
  label: string
  description: string
  url: string
}

const { PICKUP_TRUCK, SEDAN, SUV_CROSSOVER, VAN, HATCHBACK, WAGON, COUPE } = CAR_BODY_TYPE_VALUES
const {
  ACURA,
  ALPHAROMEO,
  ASTON_MARTIN,
  BENTLEY,
  BMW,
  CADILLAC,
  FERRARI,
  GENESIS,
  INFINITI,
  JAGUAR,
  LAMBORGHINI,
  LEXUS,
  AUDI,
  LAND_ROVER,
  LINCOLN,
  LOTUS,
  MASERATI,
  MERCEDES,
  PORSCHE,
  ROLLS_ROYCE,
  SHELBY,
  TESLA,
  VOLVO,
} = CAR_MAKE_VALUES
const { ELECTRIC, HYBRID } = FUEL_VALUES

const SEARCH_CATEGORIES = [
  { canonicalName: 'affordable', price: { min: 0, max: 10000 } },
  { canonicalName: 'family', bodyTypes: [VAN, SUV_CROSSOVER, HATCHBACK] },
  { canonicalName: 'commuter', bodyTypes: [SEDAN, WAGON, SUV_CROSSOVER, HATCHBACK] },
  { canonicalName: 'work', bodyTypes: [PICKUP_TRUCK] },
  { canonicalName: 'ecoFriendly', fuelType: [ELECTRIC, HYBRID] },
  { canonicalName: 'classic', category: CATEGORIES.CLASSIC_CARS_CATEGORY_ID },
  { canonicalName: 'city', bodyTypes: [COUPE, HATCHBACK] },
  {
    canonicalName: 'luxury',
    price: { min: 10000 },
    makes: [
      ACURA,
      ALPHAROMEO,
      ASTON_MARTIN,
      BENTLEY,
      BMW,
      CADILLAC,
      FERRARI,
      GENESIS,
      INFINITI,
      JAGUAR,
      LAMBORGHINI,
      LEXUS,
      AUDI,
      LAND_ROVER,
      LINCOLN,
      LOTUS,
      MASERATI,
      MERCEDES,
      PORSCHE,
      ROLLS_ROYCE,
      SHELBY,
      TESLA,
      VOLVO,
    ],
  },
]

/**
 * Get data to populate Browse by Category section on the Vertical autos BRP
 */
export const getBrowseByCategoryData = (
  t: TFunction,
  routerQuery: NextRouter['query']
): AutosCategoryData[] => {
  const currUrl = getSrpComposedUrl(routerQuery)
  const { query } = qs.parseUrl(currUrl.href)

  const catData = SEARCH_CATEGORIES.map((item) => {
    const { canonicalName, bodyTypes, makes, category, price, fuelType } = item

    const i18nLabelKey = `srp:search_by_category.${canonicalName}.label`
    const i18nDescriptionKey = `srp:search_by_category.${canonicalName}.description`

    const imgSrc = `/next-assets/images/autos-categories/${canonicalName}.jpg`

    let seoUrl = currUrl.href
    if (price) {
      seoUrl = addPriceParamToUrl(seoUrl, price)
    }

    if (bodyTypes) {
      seoUrl = addAttributeParamToUrl(AttributeType.BODY_TYPE, seoUrl, bodyTypes, t)
    }

    if (makes) {
      seoUrl = addAttributeParamToUrl(AttributeType.MAKE, seoUrl, makes, t)
    }

    if (fuelType) {
      seoUrl = addAttributeParamToUrl(AttributeType.FUEL_TYPE, seoUrl, fuelType, t)
    }

    if (category) {
      seoUrl = changeCategoryOfUrl(seoUrl, canonicalName, category, t)
    }

    return {
      description: t(i18nDescriptionKey),
      imgSrc: imgSrc,
      label: t(i18nLabelKey),
      url: qs.stringifyUrl({ url: seoUrl, query }),
    }
  })

  return catData
}
